import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import Button from "../shared/TailwindComponents/Button";
import apis from "../../utils/apis";

type Status =
  | "loading"
  | "invalid"
  | "expired"
  | "pending"
  | "accepted"
  | "rejected"
  | "license_inactive";

interface ValidateResponse {
  status: Status | "accepted";
  redirectTo?: string;
  inviterName?: string;
  casePreview?: {
    caseId: string;
    caseName: string;
    caseType: string;
    country: string;
    language: string;
    lawyerName: string;
    organizationName: string;
    progressStatus: string;
    stage: string;
    summary: string;
  };
  accessLevel?: string;
  expiresAt?: string;
  message?: string;
}


const Skeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`animate-fadePulse bg-gray-200 dark:bg-gray-700 ${className}`}></div>
);
const CaseDetailsSkeleton: React.FC = () => (
  <div className="p-4 space-y-4">
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-1/2" />
      <Skeleton className="h-6 w-20" />
    </div>
    <div className="flex space-x-2">
      <Skeleton className="h-6 w-24" />
      <Skeleton className="h-6 w-16" />
      <Skeleton className="h-6 w-32" />
    </div>
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-64 w-full" />
  </div>
);

const InvitationScreen: React.FC = () => {
  const { caseId, token } = useParams<any>();
  const history = useHistory();
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>("loading");
  const [details, setDetails] = useState<ValidateResponse | null>(null);
  const [submitting, setSubmitting] = useState<"accept" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const missingParams = useMemo(() => !token || !caseId, [token, caseId]);

  useEffect(() => {
    const run = async () => {
      if (missingParams) {
        setStatus("invalid");
        return;
      }
      setStatus("loading");
      try {
        const res = await apis.post("/external/collaborator/invitation/validate", {
          token,
          caseId,
        });
        const data = res?.data as ValidateResponse;

        if (data?.status === "accepted" && data.redirectTo) {
          history.replace(data.redirectTo);
          return;
        }

        setDetails(data);
        setStatus((data?.status as Status) || "pending");
      } catch (e: any) {
        setStatus("expired");
        setError(e?.response?.data?.error || "Invitation no longer valid.");
      }
    };
    run();
  }, [token, caseId, missingParams, history]);

  const handleAction = async (action: "accept" | "reject") => {
    if (!token || !caseId) return;
    setSubmitting(action);
    try {
      const res = await apis.post(`/external/collaborator/invitation/${action}`, { token, caseId });
      const data = res?.data as ValidateResponse;

      if (action === "accept" && data?.redirectTo) {
        history.replace(data.redirectTo);
        return;
      }
      setDetails(data);
      setStatus(action === "accept" ? "accepted" : "rejected");
    } catch (e: any) {
      if (!e) return
      setError("Couldn’t complete action, try again.");
    } finally {
      setSubmitting(null);
    }
  };

  const ExpiryBadge = ({ iso }: { iso?: string }) => {
    if (!iso) return null;
    const d = new Date(iso);
    return (
      <span className="inline-flex items-center rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-200 px-3 py-1 text-xs font-medium border border-yellow-200 dark:border-yellow-800">
        {t('Expires:')} {d.toLocaleString()}
      </span>
    );
  };

  if (status === "loading") {
    return (
      <CaseDetailsSkeleton />
    );
  }

  if (status === "invalid" || status === "expired") {
    return (
      <div className="max-w-7xl mx-auto mt-40 p-10 text-center bg-white dark:bg-gray-900 rounded-2xl shadow">
        <h2 className="text-2xl font-bold text-red-600 dark:text-red-400">
          {status === "expired" ? t('Invitation Expired') : t('Invalid Invitation')}
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          {error || t('This link is not valid anymore.')}
        </p>
        <Button className="mt-6" variant="neutral" onClick={() => history.replace("/")}>
          {t('Back to Home')}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto mt-40 p-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="px-8 py-6 bg-gradient-to-r from-sky-500/10 to-sky-600/10 dark:from-sky-700/20 dark:to-sky-800/20 border-b border-gray-200 dark:border-gray-800">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {t('Case Collaboration Invitation')}
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {details?.inviterName
              ? `${details.inviterName} ${t('invited you to collaborate')}.`
              : t("You've been invited to join a case.")}
          </p>
        </div>

        {/* Case Preview */}
        {details?.casePreview && (
          <div className="px-8 py-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <dl className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t('Case Name')}
              </dt>
              <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                {details.casePreview.caseName}
              </dd>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {details.casePreview.summary}
              </p>
            </dl>
            <dl className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t('Type / Stage')}
              </dt>
              <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                {details.casePreview.caseType} – {details.casePreview.stage}
              </dd>
              <dt className="mt-3 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t('Organization')}
              </dt>
              <dd className="mt-1 text-sm">{details.casePreview.organizationName}</dd>
              <dt className="mt-3 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t('Lawyer')}
              </dt>
              <dd className="mt-1 text-sm">{details.casePreview.lawyerName}</dd>
            </dl>
          </div>
        )}

        <div className="px-8 py-5 border-t border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200 px-3 py-1 text-xs font-medium border border-blue-200 dark:border-blue-800">
              {status === "pending" ? "Pending" : status}
            </span>
            <ExpiryBadge iso={details?.expiresAt} />
          </div>
          {status === "pending" && (
            <div className="flex gap-3">
              <Button
                variant="danger"
                onClick={() => handleAction("reject")}
                disabled={submitting !== null}
              >
                {submitting === "reject" ? t('Rejecting...') : t('Reject')}
              </Button>
              <Button
                variant="primary"
                onClick={() => handleAction("accept")}
                disabled={submitting !== null}
              >
                {submitting === "accept" ? t('Accepting...') : t('Accept & Continue')}
              </Button>
            </div>
          )}
        </div>
      </div>
      <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
        By accepting, you agree to the case’s access level and your activity may be audited.
      </p>
    </div>
  );
};

export default InvitationScreen;
